import { eplStyles } from "../clientstyles/commonstyles";
import { applyAcceptToEditor, cleanupItem } from './SuggestionAction';
import "./AutoSuggestion.css"
import { epl } from "../clientstyles/epl";
import { edp } from "../clientstyles/edp";
import { PeToolsStyles } from "../clientstyles/PeToolStyles";
import { CeToolStyles } from "../clientstyles/CeToolStyles";
import React from "react";
import { createRoot } from 'react-dom/client';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import { PopupDiff } from "./PopupDiff";
export let globalAnchorList = [];
export function addToGlobalAnchorList(item) {
    globalAnchorList.push(item);
}
let TempprojectId = null;
let TempfileName = null;
// Keeping the array reference for internal evaluations
export let globalAcceptedRegionalSpellings = [];

let currentAcceptedSpellingsKey = null;
let currentAcceptedProjectId = null;
let currentAcceptedFileName = null;
let hasLoadedCurrentAcceptedSpellings = false;

export function clearallglobalanchorlist(session = null) {
    globalAnchorList = [];
    globalRejectedAnchorList = [];
    detachAcceptedSpellings(globalAcceptedRegionalSpellings, session);
    globalAcceptedRegionalSpellings = [];
    currentAcceptedSpellingsKey = null;
    currentAcceptedProjectId = null;
    currentAcceptedFileName = null;
    hasLoadedCurrentAcceptedSpellings = false;
}

// ── Persistent Storage Operations ──
function getStorageKey(projectId, fileName) {
    return `regionalSpellings_${projectId}_${fileName}`;
}

const ACCEPTED_SPELLING_MARKER_CLASS = 'ace-AcceptedSpelling-highlight';

function ensureAcceptedSpellingStyleInjected() {
    if (typeof document === 'undefined') return;
    const styleId = 'accepted-spelling-highlight-styles';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    // Mild, low-contrast highlight — distinguishable from the regular
    // suggestion highlight (ace-Common-highlight) without being loud.
    style.innerHTML = `
        .${ACCEPTED_SPELLING_MARKER_CLASS} {
            position: absolute;
            background-color: rgba(80, 200, 120, 0.18);
            border-bottom: 1px dashed rgba(60, 160, 90, 0.55);
            pointer-events: none;
        }
    `;
    document.head.appendChild(style);
}

function addAcceptedSpellingMarker(session, entry) {
    try {
        ensureAcceptedSpellingStyleInjected();
        const Range = window.ace.require('ace/range').Range;

        const marker = {
            update(html, markerLayer, renderSession, config) {
                const startRow = entry.startAnchor.row;
                const startCol = entry.startAnchor.column;
                const endRow = entry.endAnchor.row;
                const endCol = entry.endAnchor.column;
                if (startRow == null || endRow == null) return;

                const range = new Range(startRow, startCol, endRow, endCol);
                if (range.isEmpty()) return;

                if (startRow === endRow) {
                    const screenRange = range.toScreenRange(renderSession);
                    markerLayer.drawSingleLineMarker(
                        html, screenRange, ACCEPTED_SPELLING_MARKER_CLASS, config
                    );
                } else {
                    // Defensive fallback — accepted spellings are single
                    // words in practice, but don't silently draw nothing
                    // if that ever isn't true.
                    markerLayer.drawMultiLineMarker(
                        html, range.toScreenRange(renderSession), ACCEPTED_SPELLING_MARKER_CLASS, config
                    );
                }
            }
        };

        // `true` = front layer (drawn above text background, below cursor)
        return session.addDynamicMarker(marker, true);
    } catch (err) {
        console.error("Failed to add accepted-spelling marker:", err);
        return null;
    }
}

function detachAcceptedSpellings(list, session = null) {
    list.forEach(item => {
        if (session && item.markerId != null) {
            try { session.removeMarker(item.markerId); } catch (err) { /* session may already be dead */ }
        }
        if (item.startAnchor?.detach) item.startAnchor.detach();
        if (item.endAnchor?.detach) item.endAnchor.detach();
    });
}

// *after* the last accept isn't lost.
export function persistAcceptedSpellings(projectId, fileName) {
    if (!projectId || !fileName) return;
    if (!hasLoadedCurrentAcceptedSpellings || getStorageKey(projectId, fileName) !== currentAcceptedSpellingsKey) {
        return;
    }
    const key = getStorageKey(projectId, fileName);
    const serialized = globalAcceptedRegionalSpellings
        // drop anything whose anchor got detached/invalidated
        .filter(item => item.startAnchor && item.startAnchor.row != null)
        .filter(item => {
            const sameRow = item.startAnchor.row === item.endAnchor.row;
            const validRange = item.startAnchor.row < item.endAnchor.row ||
                (sameRow && item.startAnchor.column <= item.endAnchor.column);
            if (!validRange) {
                console.warn("Dropping corrupted accepted-spelling anchor (inverted range):", item);
            }
            return validRange;
        })
        .map(item => ({
            startRow: item.startAnchor.row,
            startCol: item.startAnchor.column,
            endRow: item.endAnchor.row,
            endCol: item.endAnchor.column,
            text: item.text,
            styleName: item.styleName
        }));

    if (serialized.length === 0) {
        return;
    }

    localStorage.setItem(key, JSON.stringify(serialized));
}

export function registerAcceptedSpelling(session, startRow, startCol, endRow, endCol, newText, styleName, projectId, fileName) {
    const startAnchor = session.doc.createAnchor(startRow, startCol);
    const endAnchor = session.doc.createAnchor(endRow, endCol);
    startAnchor.$insertRight = false;
    endAnchor.$insertRight = true;

    const entry = { startAnchor, endAnchor, text: newText, styleName, markerId: null };
    entry.markerId = addAcceptedSpellingMarker(session, entry);

    // Push live memory reference
    globalAcceptedRegionalSpellings.push(entry);

    currentAcceptedSpellingsKey = getStorageKey(projectId, fileName);
    currentAcceptedProjectId = projectId;
    currentAcceptedFileName = fileName;
    hasLoadedCurrentAcceptedSpellings = true;

    persistAcceptedSpellings(projectId, fileName);
}


export function loadAcceptedSpellingsForFile(session, texFile, projectId, fileName) {
    if (!projectId || !fileName || !session) return;

    const key = getStorageKey(projectId, fileName);

    // Switched to a different file than what's loaded in memory.
    if (currentAcceptedSpellingsKey && currentAcceptedSpellingsKey !== key) {
        persistAcceptedSpellings(currentAcceptedProjectId, currentAcceptedFileName);
        detachAcceptedSpellings(globalAcceptedRegionalSpellings, session);
        globalAcceptedRegionalSpellings = [];
        hasLoadedCurrentAcceptedSpellings = false;
    }

    currentAcceptedSpellingsKey = key;
    currentAcceptedProjectId = projectId;
    currentAcceptedFileName = fileName;

    // Already have live anchors for this file in memory — nothing to do.
    if (globalAcceptedRegionalSpellings.length > 0) {
        hasLoadedCurrentAcceptedSpellings = true;
        return;
    }

    const cachedData = localStorage.getItem(key);
    if (!cachedData) {
        hasLoadedCurrentAcceptedSpellings = true;
        return;
    }

    try {
        const parsed = JSON.parse(cachedData);
        const lines = texFile.split('\n');

        globalAcceptedRegionalSpellings = parsed
            .filter(item => {
                const line = lines[item.startRow];
                if (line === undefined) return false;
                const liveText = line.substring(item.startCol, item.startCol + item.text.length);
                return liveText === item.text; // drop drifted/stale entries FROM MEMORY ONLY
            })
            .map(item => {
                const startAnchor = session.doc.createAnchor(item.startRow, item.startCol);
                const endAnchor = session.doc.createAnchor(item.endRow, item.endCol);
                startAnchor.$insertRight = false;
                endAnchor.$insertRight = true;
                const entry = {
                    startAnchor,
                    endAnchor,
                    text: item.text,
                    styleName: item.styleName,
                    markerId: null
                };
                entry.markerId = addAcceptedSpellingMarker(session, entry);
                return entry;
            });

        // Deliberately NOT re-persisting here, even if we dropped entries.
        // This function only READS/validates for the in-memory anchor list;
        // it must never have a destructive side effect on localStorage.
        // A single mismatch (stale content fetched fresh from the backend,
        // a line-ending difference, an autosave race, etc.) would otherwise
        // silently delete legitimately-good data for every OTHER accepted
        // spelling in this file too, just because the load-time comparison
        // used possibly-not-yet-consistent text. localStorage naturally
        // self-heals on the next real accept, which always writes a fresh,
        // complete, correct snapshot from the live anchors.
    } catch (err) {
        console.error("Failed to rehydrate accepted-spellings cache:", err);
        globalAcceptedRegionalSpellings = [];
    }

    hasLoadedCurrentAcceptedSpellings = true;
}

// Call this when the user actually LEAVES this file — component unmount,
// switching to a different file/project in your file tree, etc. This is
// what makes globalAcceptedRegionalSpellings genuinely empty again, so
// loadAcceptedSpellingsForFile's simple length-check correctly triggers a
// fresh hydration (with live anchors bound to whatever new session exists)
// the next time this file is opened.
export function unloadAcceptedSpellings(session = null) {
    if (currentAcceptedProjectId && currentAcceptedFileName) {
        persistAcceptedSpellings(currentAcceptedProjectId, currentAcceptedFileName);
    }
    detachAcceptedSpellings(globalAcceptedRegionalSpellings, session);
    globalAcceptedRegionalSpellings = [];
    currentAcceptedSpellingsKey = null;
    currentAcceptedProjectId = null;
    currentAcceptedFileName = null;
    hasLoadedCurrentAcceptedSpellings = false;
}

// Call this explicitly from your file-switch / component-unmount handler
// (in addition to the automatic beforeunload flush below) so the latest
// live anchor positions are saved even if the user never triggers another
// accept after their last edit.
export function flushCurrentAcceptedSpellings() {
    if (currentAcceptedProjectId && currentAcceptedFileName) {
        persistAcceptedSpellings(currentAcceptedProjectId, currentAcceptedFileName);
    }
}

if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', flushCurrentAcceptedSpellings);
}
export function isPositionAnAcceptedSpelling(position, text) {
    globalAcceptedRegionalSpellings = globalAcceptedRegionalSpellings.filter(item => {
        return item.startAnchor && item.endAnchor;
    });

    return globalAcceptedRegionalSpellings.some(item => {
        const rowMatches = item.startAnchor.row === position.startRow;
        const colMatches = item.startAnchor.column === position.startCol;
        const textMatches = item.text.toLowerCase() === text.toLowerCase();

        return rowMatches && colMatches && textMatches;
    });
}
let globalRejectedAnchorList = [];
let globalPropsRef = null;
let activeStyles = [];
let globalRejectedRef = null;
export let globalRejectedIds = new Set();
let globalRejectedPositions = [];
let globalRejectedKeys = new Set();


let focusedItemId = null;
let focusedItemTimer = null;

export function setFocusedSuggestion(id) {
    if (focusedItemTimer) clearTimeout(focusedItemTimer);
    focusedItemId = id;
    if (id) {
        focusedItemTimer = setTimeout(() => { focusedItemId = null; }, 8000);
    }
}

export function getPositionFromIndex(text, startIndex, length) {
    let row = 0, col = 0;
    for (let i = 0; i < startIndex; i++) {
        if (text[i] === '\n') { row++; col = 0; } else col++;
    }
    const startRow = row, startCol = col;
    for (let i = startIndex; i < startIndex + length; i++) {
        if (text[i] === '\n') { row++; col = 0; } else col++;
    }
    return { startRow, startCol, endRow: row, endCol: col };
}

export const forceClearAllWithDOMCleanup = (editor) => {
    const session = editor.session;
    const frontMarkers = session.getMarkers(true);
    const backMarkers = session.getMarkers(false);

    [...Object.keys(frontMarkers), ...Object.keys(backMarkers)].forEach(id => {
        session.removeMarker(parseInt(id));
    });
    globalAnchorList.forEach(item => {
        if (item.startAnchor?.detach) item.startAnchor.detach();
        if (item.endAnchor?.detach) item.endAnchor.detach();
        item.markerId = null;
    });
    globalAnchorList = [];
    const container = editor.container;
    const markerLayers = container.querySelectorAll('.ace_layer.ace_marker-layer');
    markerLayers.forEach(layer => {
        const markerSpans = layer.querySelectorAll(
            '[class*="ace-Common"], [class*="ace-EPL"], [class*="ace-EDP"], [class*="ace-highlight"],[class*="ace-Common-highlight"],[class*="ace-misspell-highlight"]'
        );
        markerSpans.forEach(span => span.remove());
    });
    session._signal("changeBackMarker");
    session._signal("changeFrontMarker");
    editor.renderer.updateFull(true);
};

export function createAnchor(
    session, Range, position,
    originalText, suggestionText,
    markerClass, styleName, description,
    anchorlist, extraStyleInfo = {}
) {
    const id = `${styleName}__${crypto.randomUUID()}`;

    const startAnchor = session.doc.createAnchor(position.startRow, position.startCol);
    const endAnchor = session.doc.createAnchor(position.endRow, position.endCol);
    startAnchor.$insertRight = false;
    endAnchor.$insertRight = true;

    const range = new Range(position.startRow, position.startCol, position.endRow, position.endCol);
    console.log("the marker Class is :", markerClass);
    const markerId = session.addMarker(range, markerClass, "text");

    anchorlist.push({
        id,
        startAnchor,
        endAnchor,
        markerId,
        originalText,
        suggestionText,
        range,
        styleInfo: { name: styleName, description, ...extraStyleInfo },
        extraStyleInfo
    });
}

export function cleanupAnchor(session, item) {
    console.log("cleanupAnchor called for:", item.originalText, "markerId:", item.markerId);
    if (item.markerId) session.removeMarker(item.markerId);
    if (item.startAnchor?.detach) item.startAnchor.detach();
    if (item.endAnchor?.detach) item.endAnchor.detach();
}

export function removeFromGlobalAnchorList(item) {
    const i = globalAnchorList.indexOf(item);
    if (i > -1) globalAnchorList.splice(i, 1);
}

export function isAlreadyAnchored(position, styleName, existingSuggestions) {
    return existingSuggestions.some(ex =>
        ex.styleInfo.name === styleName &&
        ex.startAnchor.row === position.startRow &&
        ex.startAnchor.column === position.startCol &&
        ex.endAnchor.column === position.endCol &&
        ex.endAnchor.row === position.endRow
    );
}

export function getOverlappingAnchors(position, styleName, existingSuggestions) {
    return existingSuggestions.filter(ex =>
        ex.styleInfo.name === styleName &&
        ex.startAnchor.row === position.startRow &&
        (
            ex.startAnchor.column === position.startCol ||
            (ex.startAnchor.column >= position.startCol && ex.startAnchor.column < position.endCol) ||
            (ex.endAnchor.column > position.startCol && ex.endAnchor.column <= position.endCol)
        )
    );
}

export function isRejectedByLivePosition(position, styleName, originalText) {
    return globalRejectedAnchorList.some(item =>
        item.styleInfo?.name === styleName &&
        item.originalText === originalText &&
        item.startAnchor.row === position.startRow &&
        item.startAnchor.column === position.startCol
    );
}

// function findDeletedAnchors(texFile, existingSuggestions) {
//     const deletedIds = new Set();
//     const lines = texFile.split('\n');

//     existingSuggestions.forEach(item => {
//         const row = item.startAnchor.row;
//         const col = item.startAnchor.column;
//         const line = lines[row];

//         if (line === undefined) {
//             deletedIds.add(item.id);
//             return;
//         }
//         const textAtPosition = line.substring(col, col + item.originalText.length);
//         if (textAtPosition !== item.originalText) {
//             deletedIds.add(item.id);
//         }
//     });

//     return deletedIds;
// }
const SPELL_CHECK_STYLE = "SpellCheck";

// Styles that stream results in batches while their async execute runs.
// Add any future async style name here to enable incremental delivery.
export const INCREMENTAL_ASYNC_STYLE_CONFIG = {
    SpellCheck: { batchSize: 50 },
};

export function registerIncrementalAsyncStyle(styleName, config = { batchSize: 50 }) {
    INCREMENTAL_ASYNC_STYLE_CONFIG[styleName] = config;
}

function commitIncrementalAsyncBatch(
    { anchors = [], staleIds = null },
    { session, editor, props, asyncIsCancelled }
) {
    if (asyncIsCancelled()) {
        anchors.forEach(item => {
            if (item.markerId) session.removeMarker(item.markerId);
            if (item.startAnchor?.detach) item.startAnchor.detach();
            if (item.endAnchor?.detach) item.endAnchor.detach();
        });
        return;
    }

    if (staleIds?.size) {
        globalAnchorList
            .filter(item => staleIds.has(item.id))
            .forEach(item => cleanupAnchor(session, item));
        globalAnchorList = globalAnchorList.filter(i => !staleIds.has(i.id));
        try { props.removeStaleAutoSuggestions([...staleIds]); }
        catch (err) { console.log("Incremental stale removal error:", err); }
    }

    if (anchors.length === 0) return;

    try { props.uploadAutoSuggestions(anchors); }
    catch (err) { console.log("Incremental upload error:", err); }

    globalAnchorList = [...globalAnchorList, ...anchors];
    session._signal("changeBackMarker");
    editor.renderer.updateBackMarkers();
    editor.renderer.updateFull(true);
}

function getAnchorCol(anchor) {
    return anchor?.column ?? anchor?.col ?? 0;
}

// Re-position spell-check markers on every sync pass so highlights follow document edits.
// Returns ids of spell items whose text was changed or removed.
function refreshSpellCheckMarkers(session, Range, items, texFile) {
    const staleIds = new Set();
    const lines = texFile.split('\n');

    items
        .filter(item => item.styleInfo?.name === SPELL_CHECK_STYLE)
        .forEach(item => {
            if (!item.startAnchor || item.originalText == null) {
                staleIds.add(item.id);
                return;
            }

            const row = item.startAnchor.row;
            const col = getAnchorCol(item.startAnchor);
            const endRow = item.endAnchor?.row ?? row;
            const endCol = getAnchorCol(item.endAnchor);

            const line = lines[row];
            if (line === undefined) {
                staleIds.add(item.id);
                return;
            }

            const textAtPosition = line.substring(col, col + item.originalText.length);
            if (textAtPosition !== item.originalText) {
                staleIds.add(item.id);
                return;
            }

            if (item.markerId) session.removeMarker(item.markerId);
            const range = new Range(row, col, endRow, endCol);
            item.markerId = session.addMarker(range, "ace-misspell-highlight", "text");
        });

    return staleIds;
}

function findDeletedAnchors(texFile, existingSuggestions) {
    const deletedIds = new Set();
    const lines = texFile.split('\n');

    existingSuggestions.forEach(item => {
        // Query-only anchors have no originalText — skip text-match check.
        // Their lifecycle is managed entirely by the style's staleIds return.
        if (item.queryOnly || item.originalText == null) return;

        // Spell-check items are validated in refreshSpellCheckMarkers instead.
        if (item.styleInfo?.name === SPELL_CHECK_STYLE) return;
        if (item.styleInfo?.removeType === 'affiliationRestructure') return;
        const row = item.startAnchor.row;
        const col = item.startAnchor.column;
        const line = lines[row];

        if (line === undefined) {
            deletedIds.add(item.id);
            return;
        }
        const textAtPosition = line.substring(col, col + item.originalText.length);
        if (textAtPosition !== item.originalText) {
            deletedIds.add(item.id);
        }
    });

    return deletedIds;
}

export function acceptItem(editor, item, setTextFile, props, setAllFiles = null) {
    try {
        const liveItem = globalAnchorList.find(a => a.id === item.id) || item;
        const startRow = liveItem.startAnchor.row;
        const startCol = liveItem.startAnchor.column;
        const newTextLength = liveItem.suggestionText ? liveItem.suggestionText.length : 0;
        applyAcceptToEditor(editor, liveItem, setTextFile, setAllFiles, props);
        if (liveItem.styleInfo?.name === "Convert US Word to UK Word" ||
            liveItem.styleInfo?.name === "Convert UK Word to US Word") {

            // Calculate ending column based on replacement length
            const endCol = startCol + newTextLength;
            console.log("the accepting item having projectid and name or not?", TempprojectId, TempfileName);

            registerAcceptedSpelling(
                editor.session,
                startRow, startCol,
                startRow, endCol,
                liveItem.suggestionText,
                liveItem.styleInfo.name,
                TempprojectId,
                TempfileName
            );
        }

        // const endCol = startCol + newTextLength;
        // console.log("the accepting item having projectid and name or not?", TempprojectId, TempfileName);

        // // 2. Register for ALL styles (Notice the safe navigation '?.' on styleInfo)
        // registerAcceptedSpelling(
        //     editor.session,
        //     startRow,
        //     startCol,
        //     startRow,
        //     endCol,
        //     liveItem.suggestionText, 
        //     liveItem.styleInfo?.name || "Generic Correction", // Safe fallback if styleInfo is missing
        //     TempprojectId,
        //     TempfileName
        // );


        cleanupAnchor(editor.session, liveItem);
        removeFromGlobalAnchorList(liveItem);
        props?.removeAutoSuggestion(liveItem);
    } catch (err) {
        console.error("Accept error:", err);
    }
}

export function rejectItem(editor, item, props) {
    try {
        const liveItem = globalAnchorList.find(a => a.id === item.id) || item;

        if (liveItem.markerId) editor.session.removeMarker(liveItem.markerId);
        if (liveItem.endAnchor?.detach) liveItem.endAnchor.detach();

        liveItem.markerId = null;
        liveItem.rejected = true;

        removeFromGlobalAnchorList(liveItem);
        globalRejectedAnchorList.push(liveItem);

        const rejectedEntry = {
            id: liveItem.id,
            originalText: liveItem.originalText,
            styleName: liveItem.styleInfo?.name,
            anchorRef: liveItem.startAnchor
        };

        globalRejectedKeys.add(liveItem.id);

        if (globalRejectedRef) {
            globalRejectedRef = [...globalRejectedRef, rejectedEntry];
        } else {
            globalRejectedRef = [rejectedEntry];
        }

        props?.addAutoSuggestionReject({
            id: liveItem.id,
            originalText: liveItem.originalText,
            styleName: liveItem.styleInfo?.name
        });
        props?.removeAutoSuggestion(liveItem);
    } catch (err) {
        console.error("Reject error:", err);
    }
}

export function rejectAll() {
    globalRejectedAnchorList = [];
}
function acceptSuggestion(editor, item, setTextFile, props) {
    acceptItem(editor, item, setTextFile, props);
}

function rejectSuggestion(item, props, editor) {
    rejectItem(editor, item, props);
}

function removePopup() {
    const existing = document.getElementById('ace-suggestion-popup');
    if (existing) existing.remove();
}

// ── Helper: clean up any anchors placed so far in a cancelled run ──
function cancelRunCleanup(newAnchorList, session, props) {
    newAnchorList.forEach(item => {
        if (item.markerId) session.removeMarker(item.markerId);
        if (item.startAnchor?.detach) item.startAnchor.detach();
        if (item.endAnchor?.detach) item.endAnchor.detach();
    });
    props.StopLoading();
}

export async function AutoSuggestion(
    texFile, ace, selectedStyles = [], client,
    props, setTextFile, fileName, Allfiles,
    generation = null,
    generationRef = null,
    projectId
) {
    TempprojectId = projectId;
    TempfileName = fileName;
    // ── isCancelled: true if a newer keystroke has fired since this run started ──
    const isCancelled = () =>
        generation !== null &&
        generationRef !== null &&
        generation !== generationRef.current;

    if (!props.showSuggestion) {
        globalPropsRef = null;
        return;
    }

    const styleRegistry = {
        epl: epl,
        edp: edp,
        common: { ...eplStyles, ...epl, ...edp },
        PeToolsStyles: PeToolsStyles,
        CeToolStyles: CeToolStyles,
    };

    globalPropsRef = props.autoSuggestions;
    props.startLoading();
    globalRejectedRef = props.autoSuggestionsRejected || [];
    console.log("the client and the styles in autosuggestion is :", client, "the styles are", selectedStyles);

    if (props.view === "suggestions") {
        if (!ace?.current) { props.StopLoading(); return null; }

        const styleSource = styleRegistry[client] || styleRegistry.common;

        activeStyles = selectedStyles
            .map(name => styleSource[name])
            .filter(Boolean);

        if (activeStyles.length === 0) {
            props.StopLoading();
            return [];
        }

        // ── Split into sync styles and async-heavy background styles ──
        const ASYNC_STYLE_NAMES = new Set(["Invalid URL or DOIs", "SpellCheck"]);
        // const ASYNC_STYLE_NAMES = new Set(["Invalid URL or DOIs"]);
        const syncStyles = activeStyles.filter(s => !ASYNC_STYLE_NAMES.has(s.name));
        const asyncStyles = activeStyles.filter(s => ASYNC_STYLE_NAMES.has(s.name));

        try {
            const editor = ace.current.editor;
            const session = editor.session;
            const Range = window.ace.require('ace/range').Range;

            // ── Async gap: check immediately after ──
            await new Promise(r => setTimeout(r, 100));
            if (isCancelled()) {
                props.StopLoading();
                return [];
            }

            removePopup();

            const existingSuggestions = props.autoSuggestions || [];
            const rejectedSuggestions = props.autoSuggestionsRejected || [];
            const newAnchorList = [];
            const allStaleIds = new Set();

            // ── Run each SYNC style with cancellation check before AND after ──
            for (const style of syncStyles) {

                // ── CHECK BEFORE: bail before even starting this style ──
                if (isCancelled()) {
                    console.log(`AutoSuggestion cancelled before style "${style?.name}" — discarding ${newAnchorList.length} partial anchors`);
                    cancelRunCleanup(newAnchorList, session, props);
                    return [];
                }

                if (typeof style.execute !== 'function') continue;

                try {
                    const styleStartTime = performance.now();
                    const result = style.execute(
                        texFile, session, Range, newAnchorList,
                        fileName, Allfiles,
                        existingSuggestions,
                        rejectedSuggestions
                    );
                    const returned = (result instanceof Promise) ? await result : result;
                    // ── STOP timing ──
                    const styleEndTime = performance.now();
                    const styleDurationMs = Math.round(styleEndTime - styleStartTime);

                    // ── Stamp duration onto every anchor this style just added ──
                    newAnchorList.forEach(item => {
                        // console.log("the items of the each style in the loops is :", item);
                        if (item.styleInfo?.name === style.name && item.styleDurationMs === undefined) {
                            item.styleDurationMs = styleDurationMs;
                        }
                    });
                    console.log("the new anchorlist in the autosugggestion is :", newAnchorList);

                    // ── CHECK AFTER: async styles may have taken long enough for a new keystroke ──
                    if (isCancelled()) {
                        console.log(`AutoSuggestion cancelled after style "${style?.name}" — discarding ${newAnchorList.length} partial anchors`);
                        cancelRunCleanup(newAnchorList, session, props);
                        return [];
                    }

                    if (returned instanceof Set) {
                        returned.forEach(id => allStaleIds.add(id));
                    }
                } catch (err) {
                    console.error(`Style "${style.name}" error:`, err);
                }
            }

            // ── Final check before committing anything visible to the editor ──
            if (isCancelled()) {
                console.log(`AutoSuggestion cancelled before commit — discarding ${newAnchorList.length} anchors`);
                cancelRunCleanup(newAnchorList, session, props);
                return [];
            }

            // ── Find and clean up deleted/stale anchors ──
            const deletedIds = findDeletedAnchors(texFile, existingSuggestions);
            deletedIds.forEach(id => allStaleIds.add(id));
            cleanupStaleRejectedAnchors(texFile);

            if (allStaleIds.size > 0) {
                existingSuggestions
                    .filter(item => allStaleIds.has(item.id))
                    .forEach(item => cleanupAnchor(session, item));
                props.removeStaleAutoSuggestions([...allStaleIds]);
            }

            // ── Auto apply mode ──
            if (!props.showIsChecked && props.autoapply) {
                let temp = texFile;

                for (const style of syncStyles) {

                    // ── Cancellation inside autoapply loop ──
                    if (isCancelled()) {
                        props.StopLoading();
                        return [];
                    }

                    if (typeof style.execute !== 'function') continue;

                    try {
                        const matchList = [];

                        const fakeDoc = {
                            createAnchor: (row, col) => ({
                                row,
                                column: col,
                                $insertRight: false,
                                detach: () => { }
                            })
                        };
                        const fakeSession = {
                            doc: fakeDoc,
                            addMarker: () => null,
                            removeMarker: () => { },
                            getLine: (row) => temp.split('\n')[row] || ''
                        };
                        const FakeRange = function (sr, sc, er, ec) {
                            return { start: { row: sr, column: sc }, end: { row: er, column: ec } };
                        };

                        const result = style.execute(
                            temp, fakeSession, FakeRange, matchList,
                            fileName, Allfiles, [], []
                        );
                        if (result instanceof Promise) await result;

                        // ── Check after async autoapply step ──
                        if (isCancelled()) {
                            props.StopLoading();
                            return [];
                        }

                        if (matchList.length === 0) continue;

                        const preambleItem = matchList.find(i => i.styleInfo?.isPreambleInsertion);
                        if (preambleItem) {
                            const titleMatch = /\\title\s*\{/i.exec(temp);
                            if (titleMatch) {
                                temp = preambleItem.suggestionText + temp.slice(titleMatch.index);
                            }
                            continue;
                        }

                        const captionMoveItems = matchList.filter(i => i.styleInfo?.isCaptionMove);
                        if (captionMoveItems.length > 0) {
                            for (const item of captionMoveItems) {
                                const captionText = item.styleInfo.captionText;
                                const ci = temp.indexOf(captionText);
                                if (ci === -1) continue;
                                const afterChar = temp[ci + captionText.length];
                                const removeLen = captionText.length + (afterChar === '\n' ? 1 : 0);
                                const afterRemoval = temp.slice(0, ci) + temp.slice(ci + removeLen);
                                const incPat = /\\includegraphics\s*(?:\[[^\]]*\])?\s*\{[^}]*\}/g;
                                incPat.lastIndex = ci;
                                let incMatch = incPat.exec(afterRemoval);
                                if (!incMatch) { incPat.lastIndex = 0; incMatch = incPat.exec(afterRemoval); }
                                if (!incMatch) { temp = afterRemoval; continue; }
                                const incEndIdx = incMatch.index + incMatch[0].length;
                                temp = afterRemoval.slice(0, incEndIdx) + '\n' + captionText + afterRemoval.slice(incEndIdx);
                            }
                            continue;
                        }

                        const fullLineItems = matchList.filter(i => i.styleInfo?.removeType === 'fullLine');
                        const otherItems = matchList.filter(i => i.styleInfo?.removeType !== 'fullLine');

                        if (fullLineItems.length > 0) {
                            const lines = temp.split('\n');
                            const rowsToRemove = new Set(fullLineItems.map(i => i.startAnchor.row));
                            temp = lines.filter((_, idx) => !rowsToRemove.has(idx)).join('\n');
                        }

                        const sorted = [...otherItems].sort((a, b) => {
                            if (b.startAnchor.row !== a.startAnchor.row)
                                return b.startAnchor.row - a.startAnchor.row;
                            return b.startAnchor.column - a.startAnchor.column;
                        });

                        for (const item of sorted) {
                            const lines = temp.split('\n');
                            let si = 0;
                            for (let r = 0; r < item.startAnchor.row; r++) si += (lines[r]?.length ?? 0) + 1;
                            si += item.startAnchor.column;
                            let ei = 0;
                            for (let r = 0; r < item.endAnchor.row; r++) ei += (lines[r]?.length ?? 0) + 1;
                            ei += item.endAnchor.column;
                            const textAtPos = temp.substring(si, ei);
                            if (textAtPos !== item.originalText) {
                                console.warn(`Autoapply skip: expected "${item.originalText}" but found "${textAtPos}" at row:${item.startAnchor.row} col:${item.startAnchor.column}`);
                                continue;
                            }
                            temp = temp.substring(0, si) + item.suggestionText + temp.substring(ei);
                        }

                    } catch (err) {
                        console.error(`Autoapply style "${style.name}" error:`, err);
                    }
                }

                const currentContent = session.getValue();
                if (currentContent !== temp) {
                    session.markUndoGroup();
                    const AceRange = window.ace.require('ace/range').Range;
                    const lastRow = session.getLength() - 1;
                    const lastCol = session.getLine(lastRow).length;
                    const fullRange = new AceRange(0, 0, lastRow, lastCol);
                    session.replace(fullRange, temp);
                    session.markUndoGroup();
                }

                setTextFile(temp);
                props.autoapplyonoff();
                props.StopLoading();
                return [];
            }

            // ── Commit sync anchors into Redux ──
            try {
                if (newAnchorList.length > 0) {
                    props.uploadAutoSuggestions(newAnchorList);
                }
            } catch (err) {
                console.log("Upload error:", err);
            }

            // ── Update in-memory anchor list ──
            // Preserve AI suggestions that are not stale
            const aiSuggestions = existingSuggestions.filter(i => i.isAI && !allStaleIds.has(i.id));
            const prevById = new Map(globalAnchorList.map(i => [i.id, i]));
            let surviving = existingSuggestions
                .filter(i => !allStaleIds.has(i.id) && !i.isAI)
                .map(i => prevById.get(i.id) || i);

            // Keep spell-check highlights pinned to live anchor positions between async runs
            const spellStaleIds = refreshSpellCheckMarkers(session, Range, surviving, texFile);
            if (spellStaleIds.size > 0) {
                surviving
                    .filter(item => spellStaleIds.has(item.id))
                    .forEach(item => cleanupAnchor(session, item));
                props.removeStaleAutoSuggestions([...spellStaleIds]);
                surviving = surviving.filter(i => !spellStaleIds.has(i.id));
            }

            // Re-attach markers for AI suggestions using the streamlined look (dotted underline)
            aiSuggestions.forEach(item => {
                if (!item.markerId || !session.getMarkers()[item.markerId]) {
                    const row = item.startAnchor?.row ?? 0;
                    const col = item.startAnchor?.column ?? 0;
                    const endRow = item.endAnchor?.row ?? row;
                    const endCol = item.endAnchor?.column ?? col;
                    const range = new Range(row, col, endRow, endCol);
                    item.markerId = session.addMarker(range, "Common-highlight", "text");
                    console.log(`Re-attached streamlined text marker for AI item: ${item.id}`);
                }
            });

            globalAnchorList = [...surviving, ...aiSuggestions, ...newAnchorList];

            session._signal("changeBackMarker");
            editor.renderer.updateBackMarkers();
            editor.renderer.updateFull(true);

            // ── Setup hover listener once, refresh props ref on subsequent runs ──
            if (!editor.container._hoverListenerSetup) {
                setupHoverListener(editor, setTextFile, props);
                editor.container._hoverListenerSetup = true;
            } else {
                if (editor.container._updatePropsRef) {
                    editor.container._updatePropsRef(props);
                }
            }

            props.StopLoading();  // ← UI unlocks her
            if (asyncStyles.length > 0) {
                const capturedGen = generation;
                const capturedGenRef = generationRef;
                const capturedExisting = [...(props.autoSuggestions || [])];
                const capturedRejected = [...(props.autoSuggestionsRejected || [])];
                const asyncAnchorList = [];

                // ── Cancellation check scoped to this background run ──────────────────
                const asyncIsCancelled = () =>
                    capturedGen !== null &&
                    capturedGenRef !== null &&
                    capturedGen !== capturedGenRef.current;

                const asyncStaleIds = new Set();
                const batchCommitCtx = { session, editor, props, asyncIsCancelled };

                const runAsyncStyle = (style) => {
                    if (typeof style.execute !== 'function') return Promise.resolve();

                    const incrementalConfig = INCREMENTAL_ASYNC_STYLE_CONFIG[style.name];
                    const asyncContext = incrementalConfig ? {
                        isIncremental: true,
                        batchSize: incrementalConfig.batchSize ?? 50,
                        isCancelled: asyncIsCancelled,
                        onBatch: (payload) => {
                            commitIncrementalAsyncBatch(payload, batchCommitCtx);
                        },
                    } : null;

                    try {
                        const r = style.execute(
                            texFile, session, Range, asyncAnchorList,
                            fileName, Allfiles,
                            capturedExisting,
                            capturedRejected,
                            asyncIsCancelled,
                            asyncContext
                        );
                        return (r instanceof Promise ? r : Promise.resolve(r)).then(returned => {
                            if (returned instanceof Set) {
                                returned.forEach(id => asyncStaleIds.add(id));
                            }
                        });
                    } catch (err) {
                        console.error(`Async style "${style.name}" error:`, err);
                        return Promise.resolve();
                    }
                };

                Promise.all(asyncStyles.map(runAsyncStyle))
                    .then(() => {
                        if (asyncIsCancelled()) {
                            console.log(`[Background async styles] cancelled — discarding ${asyncAnchorList.length} anchors`);
                            asyncAnchorList.forEach(item => {
                                if (item.markerId) session.removeMarker(item.markerId);
                                if (item.startAnchor?.detach) item.startAnchor.detach();
                                if (item.endAnchor?.detach) item.endAnchor.detach();
                            });
                            return;
                        }

                        if (asyncStaleIds.size > 0) {
                            globalAnchorList
                                .filter(item => asyncStaleIds.has(item.id))
                                .forEach(item => cleanupAnchor(session, item));
                            globalAnchorList = globalAnchorList.filter(i => !asyncStaleIds.has(i.id));
                            try { props.removeStaleAutoSuggestions([...asyncStaleIds]); }
                            catch (err) { console.log("Async stale removal error:", err); }
                        }

                        // Incremental async styles commit via onBatch; only bulk-commit the rest.
                        if (asyncAnchorList.length === 0) return;

                        try { props.uploadAutoSuggestions(asyncAnchorList); }
                        catch (err) { console.log("Async upload error:", err); }

                        globalAnchorList = [...globalAnchorList, ...asyncAnchorList];
                        session._signal("changeBackMarker");
                        editor.renderer.updateBackMarkers();
                        editor.renderer.updateFull(true);
                        console.log(`[Background async styles] appended ${asyncAnchorList.length} anchors`);
                    })
                    .catch(err => console.error("Background async styles fatal error:", err));
            }

            return newAnchorList;

        } catch (error) {
            console.error("AutoSuggestion error:", error);
            props.StopLoading();
            return [];
        }

    } else {
        // ── View switched away — clear everything ──
        clearAutoSuggestions(ace);
        props.NoSuggestions();
        props.autoSuggestions?.forEach(item => {
            if (item.markerId) ace.current.editor.session.removeMarker(item.markerId);
            if (item.startAnchor?.detach) item.startAnchor.detach();
            if (item.endAnchor?.detach) item.endAnchor.detach();
        });
        props.clearAutoSuggestions();
        props.clearAutoSuggestionRejects();
        return [];
    }
}

function setupHoverListener(editor, setTextFile, props) {
    const container = editor.container;
    let currentPopup = null;
    let currentHoveredKey = null;   // cache key = sorted item-id string
    let currentHoveredItem = null;
    let isMouseOverPopup = false;
    let hidePopupTimeout = null;
    let showPopupTimeout = null;
    let propsRef = props;
    container._updatePropsRef = (newProps) => { propsRef = newProps; };

    const clearFocusOnClick = () => { setFocusedSuggestion(null); };
    container.addEventListener('click', clearFocusOnClick);
    container._clearFocusOnClick = clearFocusOnClick;

    const hoverListener = (e) => {
        // Bail if hovering over the Ace search/find box
        const searchBox = editor.container.querySelector('.ace_search');
        if (searchBox && searchBox.contains(e.target)) return;

        if (hidePopupTimeout) { clearTimeout(hidePopupTimeout); hidePopupTimeout = null; }
        if (isMouseOverPopup) return;

        const position = editor.renderer.screenToTextCoordinates(e.clientX, e.clientY);
        // const allItems = findHoveredItems(position);
        const allItems = findHoveredItems(position).filter(
            item => item.styleInfo?.name !== "SpellCheck"
        );
        let itemsToShow;
        if (focusedItemId && allItems.length > 0) {
            const pinned = allItems.find(i => i.id === focusedItemId);
            itemsToShow = pinned ? [pinned] : allItems;   // pinned if available, else all
        } else {
            itemsToShow = allItems;
        }

        if (itemsToShow.length === 0) {
            // Cursor left any suggestion — schedule popup hide
            if (currentHoveredKey && !isMouseOverPopup) {
                if (showPopupTimeout) { clearTimeout(showPopupTimeout); showPopupTimeout = null; }
                hidePopupTimeout = setTimeout(() => {
                    if (!isMouseOverPopup) {
                        currentHoveredKey = null;
                        removePopup();
                        currentPopup = null;
                    }
                }, 150);
            }
            return;
        }

        // Build a stable cache key — same set of item ids → no re-render
        const cacheKey = itemsToShow.map(i => i.id).sort().join('|');

        if (cacheKey !== currentHoveredKey) {
            if (showPopupTimeout) { clearTimeout(showPopupTimeout); showPopupTimeout = null; }
            if (currentPopup) { removePopup(); currentPopup = null; }

            currentHoveredKey = cacheKey;

            showPopupTimeout = setTimeout(() => {
                if (currentHoveredKey !== cacheKey) return; // another item took over

                if (itemsToShow.length === 1) {
                    // ── Single suggestion — existing single popup ──────────────
                    const singleItem = itemsToShow[0];
                    // Verify cursor is still over this item before showing
                    const sr = singleItem.startAnchor.row, er = singleItem.endAnchor.row;
                    const sc = singleItem.startAnchor.column, ec = singleItem.endAnchor.column;
                    const pos = editor.renderer.screenToTextCoordinates(e.clientX, e.clientY);
                    const over = sr === er
                        ? (pos.column >= sc && pos.column < ec)
                        : (pos.row >= sr && pos.row <= er);
                    if (!over) return;

                    currentPopup = showPopup(
                        e, editor, singleItem, setTextFile,
                        () => { isMouseOverPopup = false; currentHoveredKey = null; },
                        (val) => { isMouseOverPopup = val; },
                        propsRef
                    );
                } else {
                    // ── Multiple overlapping suggestions — tabbed popup ────────
                    currentPopup = showMultiPopup(
                        e, editor, itemsToShow, setTextFile,
                        () => { isMouseOverPopup = false; currentHoveredKey = null; },
                        (val) => { isMouseOverPopup = val; },
                        propsRef
                    );
                }
            }, 600); // slightly faster than before (1000ms → 600ms)
        }
    };
    // ── Close popup on any keypress in the editor ──
    const keydownListener = () => {
        if (showPopupTimeout) { clearTimeout(showPopupTimeout); showPopupTimeout = null; }
        currentHoveredItem = null;
        isMouseOverPopup = false;
        removePopup();
        currentPopup = null;
    };

    // editor.on('keydown', keydownListener);
    // container._keydownListener = keydownListener;

    const mouseLeaveListener = (e) => {
        if (showPopupTimeout) { clearTimeout(showPopupTimeout); showPopupTimeout = null; }
        if (hidePopupTimeout) { clearTimeout(hidePopupTimeout); hidePopupTimeout = null; }
        if (!isMouseOverPopup) {
            currentHoveredKey = null;
            removePopup();
            currentPopup = null;
        }
    };

    const globalMouseMoveListener = (e) => {
        const editorRect = container.getBoundingClientRect();
        const isOutside =
            e.clientX < editorRect.left || e.clientX > editorRect.right ||
            e.clientY < editorRect.top || e.clientY > editorRect.bottom;

        if (isOutside && !isMouseOverPopup) {
            if (showPopupTimeout) { clearTimeout(showPopupTimeout); showPopupTimeout = null; }
            if (currentHoveredKey) {
                currentHoveredKey = null;
                removePopup();
                currentPopup = null;
            }
        }
    };

    container.addEventListener('mousemove', hoverListener);
    container.addEventListener('mouseleave', mouseLeaveListener);
    document.addEventListener('mousemove', globalMouseMoveListener);
    container.addEventListener('keydown', keydownListener);

    container._hoverListener = hoverListener;
    container._mouseLeaveListener = mouseLeaveListener;
    container._globalMouseMoveListener = globalMouseMoveListener;
    container._keydownListener = keydownListener;
}


function findHoveredItems(position) {
    const rejectedIds = new Set(globalRejectedAnchorList.map(r => r.id));
    const activeSuggestions = globalAnchorList.filter(item => !rejectedIds.has(item.id));

    return activeSuggestions.filter(item => {
        if (item.queryOnly) return false;
        const startRow = item.startAnchor.row;
        const startCol = item.startAnchor.column;
        const endRow = item.endAnchor.row;
        const endCol = item.endAnchor.column;

        if (position.row < startRow || position.row > endRow) return false;
        if (startRow === endRow) {
            return position.column >= startCol && position.column <= endCol;
        }
        if (position.row === startRow) return position.column >= startCol;
        if (position.row === endRow) return endCol > 0 && position.column <= endCol;
        return true;
    });
}

// Keep old name as a wrapper for any callers that expect a single item
function findHoveredItem(position) {
    const items = findHoveredItems(position);
    if (items.length === 0) return null;
    if (items.length === 1) return items[0];
    // Focused item wins over the generic smallest-match
    if (focusedItemId) {
        const focused = items.find(i => i.id === focusedItemId);
        if (focused) return focused;
    }
    return items.reduce((best, item) =>
        getAnchorSize(item) < getAnchorSize(best) ? item : best
    );
}

function getAnchorSize(item) {
    const startRow = item.startAnchor.row;
    const startCol = item.startAnchor.column;
    const endRow = item.endAnchor.row;
    const endCol = item.endAnchor.column;

    if (startRow === endRow) return endCol - startCol;
    return (endRow - startRow) * 10000 + endCol - startCol;
}



function showMultiPopup(mouseEvent, editor, items, setTextFile, onPopupClose, setMouseOverPopup, props) {
    removePopup();

    const popupContainer = document.createElement('div');
    popupContainer.className = 'ace-suggestion-popup-container';
    popupContainer.id = 'ace-suggestion-popup';


    const wrapper = document.createElement('div');
    wrapper.className = 'ace-suggestion-popup';
    wrapper.addEventListener('wheel', e => e.stopPropagation(), { passive: true });
    Object.assign(wrapper.style, {
        height: 'auto',
        maxHeight: '520px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    });

    // ── Close button ──────────────────────────────────────────────────────────
    const closeBtn = document.createElement('button');
    closeBtn.className = 'popup-close-btn';
    closeBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>`;
    closeBtn.addEventListener('click', () =>
        handleClose(popupContainer, null, setMouseOverPopup, onPopupClose)
    );
    wrapper.appendChild(closeBtn);

    // ── Header: "N suggestions at this location" ──────────────────────────────
    const header = document.createElement('div');
    Object.assign(header.style, {
        flexShrink: '0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '9px 36px 9px 12px',
        background: 'linear-gradient(135deg, #fff8f0 0%, #fff 100%)',
        borderBottom: '1.5px solid #f0e0cc',
    });
    const accentBar = document.createElement('div');
    Object.assign(accentBar.style, {
        flexShrink: '0',
        width: '3px',
        height: '20px',
        borderRadius: '2px',
        background: 'linear-gradient(180deg, #f47c0d 0%, #e25c00 100%)',
    });
    const headerText = document.createElement('span');
    headerText.textContent = `${items.length} suggestions at this location`;
    Object.assign(headerText.style, {
        flex: '1',
        fontSize: '12px',
        fontWeight: '700',
        color: '#7a3800',
    });
    header.appendChild(accentBar);
    header.appendChild(headerText);
    wrapper.appendChild(header);

    // ── Tab bar ───────────────────────────────────────────────────────────────
    const tabBar = document.createElement('div');
    Object.assign(tabBar.style, {
        flexShrink: '0',
        display: 'flex',
        borderBottom: '1px solid #e8e8e8',
        backgroundColor: '#fafafa',
        overflowX: 'auto',
    });

    // ── Panel container — fills remaining space and scrolls if needed ─────────
    const panelContainer = document.createElement('div');
    Object.assign(panelContainer.style, {
        flex: '1',
        overflow: 'hidden',
        position: 'relative',
    });

    const panels = items.map((item, idx) => {

        // ── Tab button ────────────────────────────────────────────────────────
        const tab = document.createElement('button');
        const tabLabel = item.styleInfo?.name
            ? (item.styleInfo.name.length > 20
                ? item.styleInfo.name.slice(0, 18) + '…'
                : item.styleInfo.name)
            : `Suggestion ${idx + 1}`;
        tab.textContent = tabLabel;
        tab.title = item.styleInfo?.description || '';
        Object.assign(tab.style, {
            padding: '7px 12px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            borderBottom: '2px solid transparent',
            background: 'transparent',
            color: '#888',
            transition: 'all 0.15s',
        });
        tabBar.appendChild(tab);


        const panel = document.createElement('div');
        Object.assign(panel.style, {
            display: idx === 0 ? 'flex' : 'none',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
        });


        const panelHeader = document.createElement('div');
        Object.assign(panelHeader.style, {
            flexShrink: '0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 10px 6px 12px',
            borderBottom: '1px solid #f0f0f0',
            background: '#fdfcfc',
        });

        const desc = document.createElement('span');
        desc.textContent = item.styleInfo?.description || '';
        Object.assign(desc.style, {
            flex: '1',
            fontSize: '12px',
            fontWeight: '600',
            color: '#333',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        });

        // FIX 1 + FIX 3: Sidebar button per panel, with reliable navigation
        const sidebarBtn = document.createElement('button');
        sidebarBtn.title = 'Go to this suggestion in sidebar';
        sidebarBtn.innerHTML = `
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
            <span>Sidebar</span>`;
        Object.assign(sidebarBtn.style, {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            flexShrink: '0',
            padding: '3px 8px',
            fontSize: '11px',
            fontWeight: '600',
            color: '#4a90e2',
            background: 'white',
            border: '1.5px solid #c7d9f5',
            borderRadius: '20px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'background 0.15s, border-color 0.15s',
        });
        sidebarBtn.onmouseover = () => {
            sidebarBtn.style.background = '#eef4ff';
            sidebarBtn.style.borderColor = '#4a90e2';
        };
        sidebarBtn.onmouseout = () => {
            sidebarBtn.style.background = 'white';
            sidebarBtn.style.borderColor = '#c7d9f5';
        };
        sidebarBtn.addEventListener('click', (e) => {
            e.stopPropagation();


            setFocusedSuggestion(item.id);


            window.dispatchEvent(
                new CustomEvent('NAVIGATE_TO_SIDEBAR_SUGGESTION', { detail: item })
            );

            // Close popup so user lands cleanly on the sidebar
            handleClose(popupContainer, null, setMouseOverPopup, onPopupClose);
        });

        panelHeader.appendChild(desc);
        panelHeader.appendChild(sidebarBtn);
        panel.appendChild(panelHeader);

        // ── Diff mount — scrollable content area ──────────────────────────────
        const diffMount = document.createElement('div');
        diffMount.className = 'popup-diff-mount';
        Object.assign(diffMount.style, {
            flex: '1',
            height: 'auto',
            maxHeight: '200px',
            overflowY: 'auto',
        });
        panel.appendChild(diffMount);

        const root = createRoot(diffMount);
        root.render(React.createElement(PopupDiff, {
            oldText: item.originalText ?? '',
            newText: item.suggestionText ?? '',
        }));
        panel._reactRoot = root;

        const actions = document.createElement('div');
        actions.className = 'popup-actions';
        Object.assign(actions.style, {
            flexShrink: '0',
            display: 'flex',
            width: '100%',
            background: 'white',
            borderTop: '1px solid #f0f0f0',
        });

        const acceptBtn = document.createElement('button');
        acceptBtn.className = 'popup-btn accept';
        acceptBtn.innerHTML = `<svg width="13" height="13" viewBox="0 0 16 16" fill="none">
            <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="currentColor"
            stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg> Accept`;
        acceptBtn.addEventListener('click', () => {
            acceptSuggestion(editor, item, setTextFile, props);
            handleClose(popupContainer, null, setMouseOverPopup, onPopupClose);
        });

        const rejectBtn = document.createElement('button');
        rejectBtn.className = 'popup-btn reject';
        rejectBtn.innerHTML = `<svg width="13" height="13" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg> Ignore`;
        rejectBtn.addEventListener('click', () => {
            rejectSuggestion(item, props, editor);
            handleClose(popupContainer, null, setMouseOverPopup, onPopupClose);
        });

        actions.appendChild(acceptBtn);
        actions.appendChild(rejectBtn);
        panel.appendChild(actions);
        panelContainer.appendChild(panel);

        // ── Tab click ─────────────────────────────────────────────────────────
        tab.addEventListener('click', () => {
            Array.from(tabBar.children).forEach((t, i) => {
                const active = i === idx;
                t.style.borderBottomColor = active ? '#f47c0d' : 'transparent';
                t.style.color = active ? '#f47c0d' : '#888';
                t.style.background = active ? 'white' : 'transparent';
                panels[i].panel.style.display = active ? 'flex' : 'none';
            });
        });

        return { tab, panel };
    });

    // ── Activate first tab ────────────────────────────────────────────────────
    if (panels[0]) {
        panels[0].tab.style.borderBottomColor = '#f47c0d';
        panels[0].tab.style.color = '#f47c0d';
        panels[0].tab.style.background = 'white';
    }

    wrapper.appendChild(tabBar);
    wrapper.appendChild(panelContainer);
    wrapper._reactRoots = panels.map(p => p.panel._reactRoot).filter(Boolean);
    popupContainer.appendChild(wrapper);

    // ── Mount off-screen, then measure and position ───────────────────────────
    popupContainer.style.cssText = `
        visibility: hidden;
        position: absolute;
        top: -9999px;
        left: -9999px;
        z-index: 1000;
    `;
    editor.container.appendChild(popupContainer);

    popupContainer.addEventListener('mouseenter', () => setMouseOverPopup(true));
    popupContainer.addEventListener('mouseleave', () => {
        setMouseOverPopup(false);
        setTimeout(() =>
            handleClose(popupContainer, null, setMouseOverPopup, onPopupClose), 300
        );
    });

    // ── Position after React paints ──────────────────────────────────────────
    setTimeout(() => {
        requestAnimationFrame(() => {
            if (!popupContainer.parentNode) return;

            const popupHeight = popupContainer.offsetHeight;
            const popupWidth = popupContainer.offsetWidth;
            const renderer = editor.renderer;
            const editorRect = editor.container.getBoundingClientRect();
            const lineHeight = renderer.lineHeight;
            const firstItem = items[0];
            const padding = 0;

            const lineScreenPos = renderer.textToScreenCoordinates(
                firstItem.startAnchor.row, firstItem.startAnchor.column
            );
            const lineTop = lineScreenPos.pageY - editorRect.top;
            const lineBottom = lineTop + lineHeight;

            // Vertical
            let top;
            if (lineTop > popupHeight + padding)
                top = lineTop - popupHeight - padding;
            else if (editorRect.height - lineBottom > popupHeight + padding)
                top = lineBottom + padding;
            else if (lineTop > editorRect.height - lineBottom)
                top = Math.max(padding, lineTop - popupHeight - padding);
            else
                top = Math.min(editorRect.height - popupHeight - padding, lineBottom + padding);

            // Horizontal — centered on mouse, clamped to editor
            const mouseX = mouseEvent.clientX - editorRect.left;
            let left = mouseX - popupWidth / 2;
            left = Math.max(padding, Math.min(left, editorRect.width - popupWidth - padding));

            top = Math.max(padding, Math.min(top, editorRect.height - popupHeight - padding));

            popupContainer.style.cssText = `
                position:   absolute;
                top:        ${top}px;
                left:       ${left}px;
                z-index:    1000;
                visibility: visible;
            `;
        });
    }, 80);

    return popupContainer;
}

function showPopup(mouseEvent, editor, item, setTextFile, onPopupClose, setMouseOverPopup, props) {
    removePopup();

    const popupContainer = document.createElement('div');
    popupContainer.className = 'ace-suggestion-popup-container';
    popupContainer.id = 'ace-suggestion-popup';

    const popup = createAutoSuggestionPopup(
        item,
        () => {
            acceptSuggestion(editor, item, setTextFile, props);
            handleClose(popupContainer, null, setMouseOverPopup, onPopupClose);
        },
        () => {
            rejectSuggestion(item, props, editor);
            handleClose(popupContainer, null, setMouseOverPopup, onPopupClose);
        },
        () => {
            handleClose(popupContainer, null, setMouseOverPopup, onPopupClose);
        }
    );

    popupContainer.appendChild(popup);

    // ── Render off-screen first so React can paint before we measure ──
    popupContainer.style.cssText = `
        visibility: hidden;
        position: absolute;
        top: -9999px;
        left: -9999px;
        z-index: 1000;
    `;
    editor.container.appendChild(popupContainer);

    popupContainer.addEventListener('mouseenter', () => setMouseOverPopup(true));
    popupContainer.addEventListener('mouseleave', () => {
        setMouseOverPopup(false);
        setTimeout(() => {
            handleClose(popupContainer, null, setMouseOverPopup, onPopupClose);
        }, 300);
    });

    // ── Wait for React (PopupDiff) to finish rendering before measuring ──
    setTimeout(() => {
        requestAnimationFrame(() => {
            // Bail if popup was closed while we were waiting
            if (!popupContainer.parentNode) return;

            const popupHeight = popupContainer.offsetHeight;
            const popupWidth = popupContainer.offsetWidth;

            const renderer = editor.renderer;
            const editorRect = editor.container.getBoundingClientRect();
            const lineHeight = renderer.lineHeight;
            const editorWidth = editorRect.width;
            const editorHeight = editorRect.height;
            const padding = 0;

            const mouseXInEditor = mouseEvent.clientX - editorRect.left;

            const { row } = item.startAnchor;
            const lineScreenPos = renderer.textToScreenCoordinates(row, item.startAnchor.column);
            const lineTop = lineScreenPos.pageY - editorRect.top;
            const lineBottom = lineTop + lineHeight;

            // ── Vertical placement ──
            let top, placement;
            if (lineTop > popupHeight + padding) {
                top = lineTop - popupHeight - padding;
                placement = "ABOVE";
            } else if (editorHeight - lineBottom > popupHeight + padding) {
                top = lineBottom + padding;
                placement = "BELOW";
            } else if (lineTop > editorHeight - lineBottom) {
                top = Math.max(padding, lineTop - popupHeight - padding);
                placement = "ABOVE-CLAMPED";
            } else {
                top = Math.min(editorHeight - popupHeight - padding, lineBottom + padding);
                placement = "BELOW-CLAMPED";
            }

            // ── Horizontal placement ──
            let left;
            const halfPopup = popupWidth / 2;
            const centeredLeft = mouseXInEditor - halfPopup;
            const spaceRight = editorWidth - mouseXInEditor;
            const spaceLeft = mouseXInEditor;

            if (centeredLeft >= padding && centeredLeft + popupWidth <= editorWidth - padding) {
                left = centeredLeft;
                placement += "-CENTER";
            } else if (spaceRight >= popupWidth + padding) {
                left = mouseXInEditor;
                placement += "-RIGHT";
            } else if (spaceLeft >= popupWidth + padding) {
                left = mouseXInEditor - popupWidth;
                placement += "-LEFT";
            } else if (spaceRight > spaceLeft) {
                left = Math.min(mouseXInEditor, editorWidth - popupWidth - padding);
                placement += "-RIGHT-CLAMPED";
            } else {
                left = Math.max(padding, mouseXInEditor - popupWidth);
                placement += "-LEFT-CLAMPED";
            }

            top = Math.max(padding, Math.min(top, editorHeight - popupHeight - padding));
            left = Math.max(padding, Math.min(left, editorWidth - popupWidth - padding));

            popupContainer.style.top = `${top}px`;
            popupContainer.style.left = `${left}px`;
            popupContainer.style.visibility = 'visible';

            if (placement.includes("ABOVE")) {
                popupContainer.classList.add('popup-above');
                popupContainer.classList.remove('popup-below');
            } else {
                popupContainer.classList.add('popup-below');
                popupContainer.classList.remove('popup-above');
            }
        });
    }, 0);   // ← one tick lets React flush its render
}



function createAutoSuggestionPopup(item, onAccept, onReject, onClose) {
    const popup = document.createElement('div');
    popup.className = 'ace-suggestion-popup';
    popup.addEventListener('wheel', (e) => e.stopPropagation(), { passive: true });
    popup.style.height = 'auto';
    popup.style.maxHeight = '500px';
    popup.style.paddingBottom = '50px';

    // ── Close button ──────────────────────────────────────────────────────────
    const closeBtn = document.createElement('button');
    closeBtn.className = 'popup-close-btn';
    closeBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>`;
    closeBtn.addEventListener('click', onClose);
    popup.appendChild(closeBtn);

    const header = document.createElement('div');
    header.className = 'popup-header';
    Object.assign(header.style, {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 40px 10px 12px',
        background: 'linear-gradient(135deg, #f0f4ff 0%, #ffffff 100%)',
        borderBottom: '1.5px solid #dde6f5',
        borderRadius: '8px 8px 0 0',
        minHeight: '44px',
    });
    const accent = document.createElement('div');
    Object.assign(accent.style, {
        flexShrink: '0',
        width: '3px',
        height: '22px',
        borderRadius: '2px',
        background: 'linear-gradient(180deg, #4a90e2 0%, #7b61ff 100%)',
    });
    const headerText = document.createElement('span');
    headerText.textContent = item.styleInfo.description;
    Object.assign(headerText.style, {
        flex: '1',
        fontSize: '12.5px',
        fontWeight: '600',
        color: '#1a2030',
        lineHeight: '1.35',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    });

    const sidebarBtn = document.createElement('button');
    sidebarBtn.title = 'View in Sidebar';
    sidebarBtn.innerHTML = `
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">
            <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
        <span>Sidebar</span>`;
    Object.assign(sidebarBtn.style, {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        flexShrink: '0',
        padding: '3px 9px',
        fontSize: '11px',
        fontWeight: '600',
        color: '#4a90e2',
        background: 'white',
        border: '1.5px solid #c7d9f5',
        borderRadius: '20px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 0.15s, border-color 0.15s',
    });
    sidebarBtn.onmouseover = () => {
        sidebarBtn.style.background = '#eef4ff';
        sidebarBtn.style.borderColor = '#4a90e2';
    };
    sidebarBtn.onmouseout = () => {
        sidebarBtn.style.background = 'white';
        sidebarBtn.style.borderColor = '#c7d9f5';
    };
    sidebarBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent('NAVIGATE_TO_SIDEBAR_SUGGESTION', { detail: item }));
    });

    header.appendChild(accent);
    header.appendChild(headerText);
    header.appendChild(sidebarBtn);
    header.setAttribute('data-title', item.styleInfo.description);
    popup.appendChild(header);


    const diffMount = document.createElement('div');
    diffMount.className = 'popup-diff-mount';
    diffMount.style.height = 'auto';
    diffMount.style.maxHeight = '250px';
    popup.appendChild(diffMount);

    const root = createRoot(diffMount);
    root.render(
        React.createElement(PopupDiff, {
            oldText: item.originalText ?? '',
            newText: item.suggestionText ?? '',
        })
    );
    popup._reactRoot = root;

    // ── Action buttons (unchanged) ────────────────────────────────────────────
    const actions = document.createElement('div');
    actions.className = 'popup-actions';
    actions.style.position = 'absolute';
    actions.style.bottom = '0';
    actions.style.display = 'flex';
    actions.style.width = '100%';
    actions.style.background = 'white';

    const acceptBtn = document.createElement('button');
    acceptBtn.className = 'popup-btn accept';
    acceptBtn.innerHTML = `<svg width="13" height="13" viewBox="0 0 16 16" fill="none">
        <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="currentColor"
        stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg> Accept`;
    acceptBtn.addEventListener('click', onAccept);

    const rejectBtn = document.createElement('button');
    rejectBtn.className = 'popup-btn reject';
    rejectBtn.innerHTML = `<svg width="13" height="13" viewBox="0 0 16 16" fill="none">
        <path d="M12 4L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <path d="M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    </svg> Ignore`;
    rejectBtn.addEventListener('click', onReject);

    actions.appendChild(acceptBtn);
    actions.appendChild(rejectBtn);
    popup.appendChild(actions);

    return popup;
}


// function handleClose(container, root, setMouseOverPopup, onPopupClose) {
//     if (document.getElementById('ace-suggestion-popup') === container) {
//         setMouseOverPopup(false);

//         // Unmount React root before removing the DOM node
//         // This prevents "Cannot update an unmounted root" React warnings
//         const popup = container.querySelector('.ace-suggestion-popup');
//         if (popup?._reactRoot) {
//             // React requires unmount to be async-safe — defer by one tick
//             setTimeout(() => popup._reactRoot.unmount(), 0);
//         }

//         container.remove();
//         if (onPopupClose) onPopupClose();
//     }
// }
// ── handleClose — handles BOTH single popup and multi-popup React roots ────────
// Replace the existing handleClose function in AutoSuggestion.js with this.
//
// Single popup: popup element has ._reactRoot
// Multi popup:  wrapper element has ._reactRoots (array, one per tab panel)
// ─────────────────────────────────────────────────────────────────────────────

function handleClose(container, root, setMouseOverPopup, onPopupClose) {
    if (document.getElementById('ace-suggestion-popup') !== container) return;

    setMouseOverPopup(false);

    // ── Unmount all React roots before removing the DOM node ─────────────────
    // Single popup root
    const singlePopup = container.querySelector('.ace-suggestion-popup');
    if (singlePopup?._reactRoot) {
        setTimeout(() => {
            try { singlePopup._reactRoot.unmount(); } catch (_) { }
        }, 0);
    }

    // Multi-popup roots (one per tab panel)
    if (singlePopup?._reactRoots?.length) {
        setTimeout(() => {
            singlePopup._reactRoots.forEach(r => {
                try { r.unmount(); } catch (_) { }
            });
        }, 0);
    }

    // Also catch any individual panel roots
    container.querySelectorAll('[class="popup-diff-mount"]').forEach(el => {
        if (el._reactRoot) {
            setTimeout(() => {
                try { el._reactRoot.unmount(); } catch (_) { }
            }, 0);
        }
    });

    container.remove();
    if (onPopupClose) onPopupClose();
}

function clearPreviousMarkers(ace) {
    if (!ace?.current || globalAnchorList.length === 0) return;
    const session = ace.current.editor.session;
    globalAnchorList?.forEach(item => {
        if (item.markerId) session.removeMarker(item.markerId);
        if (item.startAnchor?.detach) item.startAnchor.detach();
        if (item.endAnchor?.detach) item.endAnchor.detach();
    });
    globalAnchorList = [];
}

function cleanupStaleRejectedAnchors(texFile) {
    const lines = texFile.split('\n');
    const toDetach = [];

    globalRejectedAnchorList = globalRejectedAnchorList.filter(item => {
        const row = item.startAnchor.row;
        const col = item.startAnchor.column;
        const line = lines[row];

        if (line === undefined) { toDetach.push(item); return false; }

        const textAtPos = line.substring(col, col + item.originalText.length);
        if (textAtPos !== item.originalText) { toDetach.push(item); return false; }

        return true;
    });

    toDetach.forEach(item => {
        if (item.startAnchor?.detach) item.startAnchor.detach();
    });
}

export function clearAutoSuggestions(ace) {
    if (!ace?.current) return;
    const session = ace.current.editor.session;
    const container = ace.current.editor.container;
    if (container._keydownListener) {
        ace.current.editor.off('keydown', container._keydownListener);
        // container.removeEventListener('keydown', container._keydownListener);
        delete container._keydownListener;
    }

    if (container._hoverListener) {
        container.removeEventListener('mousemove', container._hoverListener);
        delete container._hoverListener;
    }
    if (container._mouseLeaveListener) {
        container.removeEventListener('mouseleave', container._mouseLeaveListener);
        delete container._mouseLeaveListener;
    }
    if (container._globalMouseMoveListener) {
        document.removeEventListener('mousemove', container._globalMouseMoveListener);
        delete container._globalMouseMoveListener;
    }
    delete container._hoverListenerSetup;

    removePopup();

    globalAnchorList.forEach(item => {
        if (item.markerId) session.removeMarker(item.markerId);
        if (item.startAnchor?.detach) item.startAnchor.detach();
        if (item.endAnchor?.detach) item.endAnchor.detach();
    });
    globalRejectedAnchorList.forEach(item => {
        if (item.startAnchor?.detach) item.startAnchor.detach();
    });
    globalAnchorList = [];
    activeStyles = [];
    globalRejectedAnchorList = [];
    globalRejectedRef = null;
    globalRejectedIds = new Set();
    globalRejectedPositions = [];
    globalRejectedKeys = new Set();
}

export const forceClearAll = (editor) => {
    globalAnchorList.forEach(item => {
        if (item.markerId) editor.session.removeMarker(item.markerId);
    });
    globalAnchorList = []; // Clear the internal tracking list
    editor.session._signal("changeBackMarker");
};

export function getAvailableStyles() {
    return Object.keys(eplStyles).map(key => ({
        id: key,
        name: eplStyles[key].name,
        description: eplStyles[key].description
    }));
}